import { Color, MeshStandardMaterial, Vector4, Texture, type WebGLProgramParametersWithUniforms } from 'three';
import { WORLD, METERS_PER_TEXEL, TERRAIN_LOOK } from '../config';
import type { TerrainTextureSet } from './TerrainTextures';

/**
 * Materiál terénu.
 *
 * Postavený nad MeshStandardMaterial (tj. plné PBR, IBL, stíny, mlha zdarma),
 * doplněný o dvě věci přes onBeforeCompile:
 *
 *  1) Vertex shader posouvá vrcholy podle výškové mapy a počítá normálu.
 *     Díky tomu je geometrie chunků sdílená a LOD switch je jen záměna bufferu.
 *  2) Fragment shader míchá vrstvy povrchu podle sklonu a nadmořské výšky
 *     a přidává detailní normálu z šumu. Až budou k dispozici skutečné PBR
 *     textury (ambientCG / Poly Haven, CC0), nahradí se tady jen tSurface().
 */

const VERT_HEAD = /* glsl */ `
uniform sampler2D uHeightMap;
uniform float uHeightRes;
uniform float uWorldSize;
uniform float uTexel;

attribute float aSkirt;

varying vec3 vWorldPos;
varying vec3 vTerrainNormal;

float tFetch(ivec2 c) {
  ivec2 m = ivec2(int(uHeightRes) - 1);
  return texelFetch(uHeightMap, clamp(c, ivec2(0), m), 0).r;
}

// Ruční bilineární filtrace: float textury nejsou všude filtrovatelné,
// texelFetch je vždy a navíc bez ztráty přesnosti.
float tHeightAt(vec2 wxz) {
  vec2 t = (wxz + uWorldSize * 0.5) / uTexel;
  vec2 f = fract(t);
  ivec2 i0 = ivec2(floor(t));
  float h00 = tFetch(i0);
  float h10 = tFetch(i0 + ivec2(1, 0));
  float h01 = tFetch(i0 + ivec2(0, 1));
  float h11 = tFetch(i0 + ivec2(1, 1));
  return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
}

vec3 tNormalAt(vec2 wxz) {
  float e = uTexel;
  float hl = tHeightAt(wxz - vec2(e, 0.0));
  float hr = tHeightAt(wxz + vec2(e, 0.0));
  float hd = tHeightAt(wxz - vec2(0.0, e));
  float hu = tHeightAt(wxz + vec2(0.0, e));
  return normalize(vec3(hl - hr, 2.0 * e, hd - hu));
}
`;

const FRAG_HEAD = /* glsl */ `
uniform float uSeaLevel;
uniform float uSandLevel;
uniform vec3 uSandTint;
uniform float uDetailDistance;
uniform float uMacroDistance;

uniform sampler2D uGrassColor;
uniform sampler2D uGrassNormal;
uniform sampler2D uRockColor;
uniform sampler2D uRockNormal;
uniform sampler2D uDirtColor;
uniform sampler2D uDirtNormal;
uniform sampler2D uSandColor;
uniform sampler2D uSandNormal;
uniform float uTileSand;
uniform sampler2D uSnowColor;
uniform sampler2D uSnowNormal;
uniform sampler2D uRoughPacked;
uniform vec4 uTileScale;   // metry na jedno opakování: tráva, skála, hlína, sníh
uniform float uNormalStrength;
uniform float uSnowStart;
uniform float uSnowFull;

varying vec3 vWorldPos;
varying vec3 vTerrainNormal;

float tHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float tValueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(tHash(i), tHash(i + vec2(1.0, 0.0)), u.x),
             mix(tHash(i + vec2(0.0, 1.0)), tHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float tFbm(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) { s += a * tValueNoise(p); p *= 2.07; a *= 0.5; }
  return s;
}

/**
 * Normálová mapa bez tangentového atributu.
 *
 * Dlaždice jsou promítané rovnoběžně se světovými osami X a Z, takže
 * tangentová báze je známá bez počítání: T je světové +X promítnuté do
 * roviny povrchu, B je na něj kolmé. Ušetří to tangent atribut i jeho
 * přepočet při každé změně LOD.
 */
vec3 tApplyNormalMap(vec3 n, vec3 mapSample, float strength) {
  vec3 t = normalize(cross(vec3(0.0, 0.0, 1.0), n) + vec3(1e-5, 0.0, 0.0));
  vec3 b = cross(n, t);
  vec3 m = mapSample * 2.0 - 1.0;
  m.xy *= strength;
  return normalize(t * m.x + b * m.y + n * m.z);
}

/**
 * Skála na srázu. Rovinné promítání do XZ se na svislé stěně natáhne do
 * nudlí, proto se tam přimíchá druhá projekce ("stěnová"). Dva vzorky
 * místo tří u plné triplanární projekce — na terén to vizuálně stačí.
 */
vec4 tBiplanar(sampler2D tex, vec3 wp, float scale, float wall) {
  vec4 flat3 = texture2D(tex, wp.xz * scale);
  if (wall < 0.01) return flat3;
  vec4 side = texture2D(tex, vec2((wp.x + wp.z) * 0.7071, -wp.y) * scale);
  return mix(flat3, side, wall);
}

vec3 tSurface(out float roughOut, out vec3 normalOut) {
  vec3 n = normalize(vTerrainNormal);
  float slope = 1.0 - n.y;
  float h = vWorldPos.y;
  float dist = length(vWorldPos - cameraPosition);
  vec3 wp = vWorldPos;

  // Makro variace rozbíjí opakování dlaždic — bez ní je i dobrá textura
  // na velké ploše okamžitě čitelná jako mřížka.
  float macro = tFbm(wp.xz * 0.0035);
  float meso = tFbm(wp.xz * 0.045);
  float jitter = (meso - 0.5) * 8.0;

  // --- váhy vrstev ---
  float wRock = smoothstep(0.13, 0.44, slope + jitter * 0.012);
  float wDirt = smoothstep(0.05, 0.22, slope + jitter * 0.010) * (1.0 - wRock);
  float wSnow = smoothstep(uSnowStart + jitter * 3.0, uSnowFull, h)
              * (1.0 - smoothstep(0.34, 0.58, slope));
  float wBeach = 1.0 - smoothstep(uSeaLevel + 1.0, uSandLevel + jitter, h);
  float wGrass = max(0.0, 1.0 - wRock - wDirt);

  float wallBlend = smoothstep(0.30, 0.78, slope);

  // --- barvy ---
  vec4 packedRough = texture2D(uRoughPacked, wp.xz * uTileScale.x);
  vec3 color = vec3(0.0);
  vec3 nrm = n;
  float rough = 0.9;

  vec3 grass = texture2D(uGrassColor, wp.xz * uTileScale.x).rgb;
  color = grass;
  rough = packedRough.r;
  nrm = tApplyNormalMap(n, texture2D(uGrassNormal, wp.xz * uTileScale.x).rgb, uNormalStrength);

  if (wDirt > 0.004) {
    vec3 dirt = texture2D(uDirtColor, wp.xz * uTileScale.z).rgb;
    color = mix(color, dirt, wDirt);
    rough = mix(rough, texture2D(uRoughPacked, wp.xz * uTileScale.z).b, wDirt);
    nrm = normalize(mix(nrm, tApplyNormalMap(n, texture2D(uDirtNormal, wp.xz * uTileScale.z).rgb, uNormalStrength), wDirt));
  }

  if (wRock > 0.004) {
    vec3 rock = tBiplanar(uRockColor, wp, uTileScale.y, wallBlend).rgb;
    color = mix(color, rock, wRock);
    rough = mix(rough, tBiplanar(uRoughPacked, wp, uTileScale.y, wallBlend).g, wRock);
    nrm = normalize(mix(nrm, tApplyNormalMap(n, tBiplanar(uRockNormal, wp, uTileScale.y, wallBlend).rgb, uNormalStrength * 1.2), wRock));
  }

  if (wBeach > 0.004) {
    // Pláž má vlastní texturu.
    //
    // Dřív se pro ni brala hlína a odbarvovala se do béžova. Ušetřilo to
    // jednu sadu textur, ale písek pak měl zrno a hrudky hlíny — na pláži
    // to vypadalo jako rozdrolená země, ne jako písek. Teď je to skutečný
    // písek i s vlastní normálou, takže na něm hrají vlnky.
    vec3 sand = texture2D(uSandColor, wp.xz * uTileSand).rgb * uSandTint;
    color = mix(color, sand, wBeach);
    rough = mix(rough, 0.86, wBeach);
    nrm = normalize(mix(nrm, tApplyNormalMap(n, texture2D(uSandNormal, wp.xz * uTileSand).rgb, uNormalStrength * 0.8), wBeach));
  }

  if (wSnow > 0.004) {
    vec3 snow = texture2D(uSnowColor, wp.xz * uTileScale.w).rgb;
    color = mix(color, snow, wSnow);
    rough = mix(rough, texture2D(uRoughPacked, wp.xz * uTileScale.w).a, wSnow);
    nrm = normalize(mix(nrm, tApplyNormalMap(n, texture2D(uSnowNormal, wp.xz * uTileScale.w).rgb, uNormalStrength * 0.7), wSnow));
  }

  // Velkoplošná variace jasu a odstínu — tohle dělá největší kus práce
  // proti tomu, aby terén vypadal jako tapeta.
  color *= 0.78 + 0.44 * macro;
  color *= 0.88 + 0.24 * meso;

  // Musí se měnit i ODSTÍN, ne jen jas.
  //
  // Samotné rozjasňování a ztmavování jedné zelené dá z dálky pořád jednu
  // zelenou, jen místy světlejší — svah pak vypadá jako potažený filcem.
  // Skutečná krajina střídá chladnou trávu ve stínu údolí s vysušenou
  // dožluta na hřbetech. Vlnová délka je schválně přes kilometr, aby to
  // fungovalo na měřítku kopců, ne na měřítku kroků.
  float tHue = tFbm(wp.xz * 0.0011 + 17.3);
  color *= mix(vec3(0.82, 0.95, 0.80), vec3(1.16, 1.03, 0.80), tHue);

  // Na dálku se detail normálové mapy stejně ztratí a jen šumí,
  // proto se povrch postupně vrací k hladké geometrické normále.
  float normalFade = 1.0 - smoothstep(uDetailDistance * 0.35, uDetailDistance, dist);
  nrm = normalize(mix(n, nrm, normalFade));

  normalOut = nrm;
  // Přirozeně matný povrch terénu (hlína, tráva) bez nepřirozeného plastového lesku.
  // Ne úplně matné: suchá tráva a písek mají slabý odlesk, který na
  // svazích proti slunci prozradí tvar terénu.
  roughOut = clamp(0.74 + rough * 0.20, 0.74, 0.96);
  return color;
}
`;

export interface TerrainMaterialUniforms {
  [key: string]: { value: unknown };
}

export interface TerrainMaterial extends MeshStandardMaterial {
  userData: { uniforms: TerrainMaterialUniforms };
}

/**
 * @param heightMap výšková mapa světa (R32F)
 * @param setupShadows volitelný hook (CSM.setupMaterial) — musí proběhnout
 *        před naším patchem, protože CSM přepisuje onBeforeCompile.
 */
export function createTerrainMaterial(
  heightMap: Texture,
  textures: TerrainTextureSet,
  setupShadows?: (m: MeshStandardMaterial) => void,
): TerrainMaterial {
  const material = new MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.95,
    metalness: 0.0,
    dithering: true,
  });

  const uniforms: TerrainMaterialUniforms = {
    uHeightMap: { value: heightMap },
    uHeightRes: { value: WORLD.heightRes },
    uWorldSize: { value: WORLD.size },
    uTexel: { value: METERS_PER_TEXEL },
    uSeaLevel: { value: WORLD.seaLevel },
    uSandLevel: { value: WORLD.seaLevel + TERRAIN_LOOK.beachHeight },
    uSandTint: { value: new Color(1.28, 1.12, 0.86) },
    uDetailDistance: { value: TERRAIN_LOOK.normalFadeDistance },
    uMacroDistance: { value: 900 },
    uGrassColor: { value: textures.grassColor },
    uGrassNormal: { value: textures.grassNormal },
    uRockColor: { value: textures.rockColor },
    uRockNormal: { value: textures.rockNormal },
    uDirtColor: { value: textures.dirtColor },
    uDirtNormal: { value: textures.dirtNormal },
    uSandColor: { value: textures.sandColor },
    uSandNormal: { value: textures.sandNormal },
    uTileSand: { value: 1 / TERRAIN_LOOK.tileSand },
    uSnowColor: { value: textures.snowColor },
    uSnowNormal: { value: textures.snowNormal },
    uRoughPacked: { value: textures.roughPacked },
    // Převrácená velikost dlaždice v metrech: 1/6 => textura se opakuje po 6 m.
    uTileScale: {
      value: new Vector4(
        1 / TERRAIN_LOOK.tileGrass,
        1 / TERRAIN_LOOK.tileRock,
        1 / TERRAIN_LOOK.tileDirt,
        1 / TERRAIN_LOOK.tileSnow,
      ),
    },
    uNormalStrength: { value: TERRAIN_LOOK.normalStrength },
    uSnowStart: { value: TERRAIN_LOOK.snowStart },
    uSnowFull: { value: TERRAIN_LOOK.snowFull },
  };

  setupShadows?.(material);
  const previous = material.onBeforeCompile;

  material.onBeforeCompile = function (shader: WebGLProgramParametersWithUniforms, renderer) {
    previous.call(material, shader, renderer);

    Object.assign(shader.uniforms, uniforms);

    shader.vertexShader = VERT_HEAD + shader.vertexShader;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <beginnormal_vertex>',
        `#include <beginnormal_vertex>
        vec2 tWorldXZ = (modelMatrix * vec4(position, 1.0)).xz;
        float tHeight = tHeightAt(tWorldXZ);
        objectNormal = tNormalAt(tWorldXZ);`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        transformed.y += tHeight - aSkirt;
        vWorldPos = vec3(tWorldXZ.x, transformed.y, tWorldXZ.y);
        vTerrainNormal = objectNormal;`,
      );

    shader.fragmentShader = FRAG_HEAD + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_fragment>',
        `float tRoughness;
        vec3 tNormal;
        diffuseColor.rgb *= tSurface(tRoughness, tNormal);`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        roughnessFactor = tRoughness;`,
      )
      .replace(
        '#include <normal_fragment_begin>',
        `#include <normal_fragment_begin>
        normal = normalize((viewMatrix * vec4(tNormal, 0.0)).xyz);`,
      );
  };

  const typed = material as TerrainMaterial;
  typed.userData = { uniforms };
  return typed;
}
